import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, type TagProps } from 'antd';
import { BLACK, BLUE, GREEN, ORANGE, RED } from 'pos-client/utils/colours.ts';
import {
  type StatusTypeCamel,
  StatusTypeEnum,
} from 'pos-shared/models/statusSchema.ts';
import { literalToCamelCase } from 'pos-shared/utils/textHelpers.ts';
import { isEnumValue } from 'pos-shared/utils/zodHelpers.ts';

export const getStatusStyling = (
  status: StatusTypeCamel
): {
  color: TagProps['color'];
  customColor?: CSSProperties;
} => {
  switch (status) {
    case 'initiated':
    case 'draft':
    case 'fleet':
    case 'dropOff':
    case 'notStarted':
    case 'requiresFm':
      return { color: undefined, customColor: BLACK };
    case 'wipBuilding':
    case 'inProgress':
    case 'inTransit':
    case 'raiseReturn':
    case 'partialReturn':
    case 'pending':
    case 'notRequired':
    case 'transferring':
    case 'transferred':
    case 'returnToSupplier':
    case 'scrap':
    case 'inactive':
    case 'open':
    case 'partiallyPaid':
    case 'onHold':
    case 'paused':
    case 'pendingApproval':
    case 'awaitingCaf':
    case 'invoiced':
    case 'awaitingFm':
    case 'notFitted':
      return { color: undefined, customColor: ORANGE };
    case 'awaitingFulfilment':
    case 'wipAwaitingFulfilment':
    case 'quotedJob':
    case 'toDo':
    case 'scheduled':
    case 'ordered':
    case 'quarantined':
    case 'speculative':
    case 'ready':
    case 'retail':
    case 'returned':
    case 'releasedToJob':
    case 'collectedBySupplier':
    case 'collectedAsScrap':
    case 'approved':
    case 'onStop':
    case 'static':
    case 'fixedTime':
    case 'assignedToJob':
    case 'allocatedToJob':
    case 'imported':
    case 'toBeComplete':
    case 'shortShipped':
      return { color: undefined, customColor: BLUE };
    case 'completed':
    case 'complete':
    case 'authorised':
    case 'authorized':
    case 'delivered':
    case 'fitted':
    case 'inStock':
    case 'active':
    case 'paid':
    case 'fulfilled':
      return { color: undefined, customColor: GREEN };
    case 'cancelled':
    case 'declined':
    case 'inProgressOverrunning':
    case 'notOrdered':
    case 'partCancelled':
    case 'failed':
    case 'writtenOff':
    case 'closed':
    case 'outOfStock':
    case 'unauthorised':
    case 'outstanding':
    case 'mobile':
    case 'removedByAdjustment':
    case 'expired':
    case 'notAuthorized':
    case 'partialAuthorization':
      return { color: undefined, customColor: RED };
    default:
      return { color: undefined, customColor: BLACK };
  }
};

interface StatusTagProps {
  closable?: boolean;
  onClose?: () => void;
  status: string;
  style?: React.CSSProperties;
  testId?: string;
}

export const StatusTag = ({
  closable,
  onClose,
  status,
  style,
  testId,
}: StatusTagProps) => {
  const { t } = useTranslation();

  const statusLiteral = isEnumValue(status, StatusTypeEnum);
  const mappedStatus = statusLiteral
    ? literalToCamelCase(statusLiteral)
    : 'unknown';
  const { color, customColor } = getStatusStyling(mappedStatus);

  return (
    <Tag
      style={{
        fontWeight: 'normal',
        ...customColor,
        ...style,
      }}
      closable={closable}
      onClose={onClose}
      color={color}
      data-testid={testId}
    >
      {mappedStatus === 'unknown' && status !== ''
        ? status
        : t(`statuses.${mappedStatus}`)}
    </Tag>
  );
};
